import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/lib/supabase';
import { TileEmpty } from '../TileWrapper';
import { PieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip } from 'recharts';
import { PieChart as PieChartIcon } from 'lucide-react';

const COLORS = {
  valid: '#22c55e',
  expiring: '#f59e0b',
  expired: '#ef4444',
  missing: '#6b7280',
};

// Mock data for simulation
const MOCK_DATA = {
  chartData: [
    { name: 'Valid', value: 156, color: COLORS.valid },
    { name: 'Expiring', value: 23, color: COLORS.expiring },
    { name: 'Expired', value: 12, color: COLORS.expired },
    { name: 'Missing', value: 9, color: COLORS.missing },
  ],
  totals: { valid: 156, expiring: 23, expired: 12, missing: 9 },
  total: 200,
};

export function RequirementsByStatusTile({ filter, size, useMockData }) {
  const { company, userProfile } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (useMockData) {
      setData(MOCK_DATA);
      setLoading(false);
      return;
    }

    if (!company?.id || !userProfile?.id) return;

    const loadData = async () => {
      setLoading(true);
      try {
        const { data: dashData, error } = await supabase.rpc('get_dashboard_data_v2', {
          p_company_id: company.id,
          p_user_id: userProfile.id,
          p_include_archived: false,
          p_limit: 100,
          p_offset: 0,
          p_search: null,
        });

        if (error) throw error;

        let projects = dashData?.projects || [];
        if (filter?.spaceId) {
          projects = projects.filter(p => p.id === filter.spaceId);
        }

        let totals = { valid: 0, expiring: 0, expired: 0, missing: 0 };

        projects.forEach(p => {
          const summary = p.compliance_summary || {};
          totals.valid += summary.valid || 0;
          totals.expiring += summary.expiring || 0;
          totals.expired += summary.expired || 0;
          totals.missing += summary.missing || 0;
        });

        const chartData = [
          { name: 'Valid', value: totals.valid, color: COLORS.valid },
          { name: 'Expiring', value: totals.expiring, color: COLORS.expiring },
          { name: 'Expired', value: totals.expired, color: COLORS.expired },
          { name: 'Missing', value: totals.missing, color: COLORS.missing },
        ].filter(d => d.value > 0);

        setData({
          chartData,
          totals,
          total: totals.valid + totals.expiring + totals.expired + totals.missing,
        });
      } catch (err) {
        console.error('Failed to load requirements status:', err);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [company?.id, userProfile?.id, filter?.spaceId, useMockData]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-pulse">
          <div className="w-32 h-32 rounded-full bg-gray-200" />
        </div>
      </div>
    );
  }

  if (!data || data.total === 0) {
    return <TileEmpty message="No requirements data" icon={PieChartIcon} />;
  }

  const isLarge = size === 'large';

  return (
    <div className="h-full flex flex-col">
      {/* Chart */}
      <div className={isLarge ? 'flex-1 min-h-[200px]' : 'h-32'}>
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              data={data.chartData}
              cx="50%"
              cy="50%"
              innerRadius={isLarge ? 50 : 30}
              outerRadius={isLarge ? 80 : 50}
              paddingAngle={2}
              dataKey="value"
            >
              {data.chartData.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={entry.color} />
              ))}
            </Pie>
            <Tooltip
              formatter={(value, name) => [value, name]}
              contentStyle={{ fontSize: '12px' }}
            />
          </PieChart>
        </ResponsiveContainer>
      </div>

      {/* Legend - horizontal for medium, vertical for large */}
      <div className={`mt-2 ${isLarge ? 'space-y-1' : 'flex flex-wrap justify-center gap-x-4 gap-y-1'}`}>
        {data.chartData.map((item) => (
          <div key={item.name} className="flex items-center gap-2">
            <div
              className="w-3 h-3 rounded-sm shrink-0"
              style={{ backgroundColor: item.color }}
            />
            <span className="text-xs text-gray-600">{item.name}</span>
            <span className="text-xs font-medium">{item.value}</span>
            {isLarge && (
              <span className="text-xs text-gray-400">
                ({Math.round((item.value / data.total) * 100)}%)
              </span>
            )}
          </div>
        ))}
      </div>

      {/* Stats bar for large */}
      {isLarge && (
        <div className="mt-4 pt-3 border-t">
          <div className="flex h-2 rounded-full overflow-hidden bg-gray-100">
            {data.chartData.map((item) => (
              <div
                key={item.name}
                style={{
                  width: `${(item.value / data.total) * 100}%`,
                  backgroundColor: item.color,
                }}
              />
            ))}
          </div>
          <p className="text-xs text-center text-gray-500 mt-2">
            {data.total} total requirements
          </p>
        </div>
      )}
    </div>
  );
}
